import * as Sentry from '@sentry/vue'
import { consoleSandbox } from '@sentry/core'

import { useKeycloak } from '@/shared/composables/useKeycloak'
import { useOrganizationStore } from '@/stores/organization'
import { OrganizationRequiredError } from '@/shared/utils/OrganizationRequired.error'

type ApiOptions = {
  baseEndpoint?: string
  joinSlash?: boolean
  /** Skip organization context requirement for this API instance */
  skipOrganizationCheck?: boolean
}

/**
 * Endpoints that don't require an organization context.
 * These are typically authentication/authorization related endpoints.
 */
const ORGANIZATION_EXEMPT_ENDPOINTS = ['/access-control']

interface ApiHeaders {
  Accept?: string
  'Content-Type'?: string
  Authorization?: string
  'X-Organization-Id'?: string
}

/** A failed Response tagged with the HTTP method that produced it. */
export type ResponseWithMethod = Response & { method?: string }

/**
 * Send a failed backend API call to Sentry. useApi is the single chokepoint
 * every request passes through, so reporting here guarantees backend errors
 * reach Sentry regardless of how the calling code handles the rejection.
 * In dev (or when no DSN is configured) the error is also logged to the
 * console for visibility; the sandboxed log bypasses captureConsoleIntegration
 * so Sentry still receives exactly one structured event.
 */
const reportApiError = (error: Error, tags: Record<string, string | number>, url: string): void => {
  if (import.meta.env.DEV || !import.meta.env.VITE_SENTRY_DSN) {
    // consoleSandbox lifts Sentry's console patching for this call, so the dev
    // log is not double-reported through captureConsoleIntegration
    consoleSandbox(() => console.error(error))
  }
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error, { tags, extra: { url } })
  }
}

/**
 * Report a non-ok HTTP response. Every status is reported (including 401/403)
 * so nothing is missed; filter out expected auth noise in Sentry instead. The
 * http_status tag makes that filtering straightforward.
 */
const reportApiResponseError = (response: ResponseWithMethod): void => {
  reportApiError(
    new Error(`API ${response.method ?? 'request'} failed (${response.status}): ${response.url}`),
    { http_status: response.status, http_method: response.method ?? 'unknown' },
    response.url
  )
}

/** Report a network/CORS failure (fetch rejected before producing a Response). */
const reportApiNetworkError = (apiUrl: string, cause: unknown): void => {
  const detail = cause instanceof Error ? cause.message : String(cause)

  reportApiError(
    new Error(`API request failed (network error): ${apiUrl} — ${detail}`),
    { http_status: 'network_error' },
    apiUrl
  )
}

type cbsetErrorHandler = (type: string) => void
let errorHandler: cbsetErrorHandler | null = null

let apiBaseUrl: string | null = null

// Module-level abort signal shared by every in-flight request. Reset by
// abortAllInFlightApiRequests, currently called from useOrganizationSwitch on
// org change so that requests fired against the previous org cannot write
// their responses into stores that now reflect the new org.
let inFlightAbortController = new AbortController()

/**
 * Abort every in-flight request issued through useApi. Subsequent requests
 * receive a fresh signal. Used when the org context changes; without this,
 * a slow response against the old org can overwrite freshly-loaded state
 * for the new org.
 */
export const abortAllInFlightApiRequests = (): void => {
  inFlightAbortController.abort()
  inFlightAbortController = new AbortController()
}

/**
 * A signal that aborts when either input aborts. The module-level in-flight
 * signal always applies; a request may add its own on top.
 */
const combineSignals = (base: AbortSignal, extra?: AbortSignal): AbortSignal => {
  if (!extra) return base
  const controller = new AbortController()
  const abort = () => controller.abort()
  if (base.aborted || extra.aborted) {
    abort()
  } else {
    base.addEventListener('abort', abort, { once: true })
    extra.addEventListener('abort', abort, { once: true })
  }
  return controller.signal
}

export type ApiRequestOptions = {
  /** Aborts this request alone, in addition to the shared org-switch abort */
  signal?: AbortSignal
}

export const useApi = (options?: ApiOptions) => {
  const { getToken, refreshToken } = useKeycloak()

  const baseEndpoint = options?.baseEndpoint || ''
  const joinSlash = options?.joinSlash || false
  const skipOrganizationCheck = options?.skipOrganizationCheck || false

  /**
   * Checks if the current endpoint requires organization context.
   * Throws OrganizationRequiredError if organization is required but not set.
   */
  const requireOrganizationContext = (): void => {
    // Skip check if explicitly disabled for this API instance
    if (skipOrganizationCheck) return

    // Skip check for exempt endpoints
    if (ORGANIZATION_EXEMPT_ENDPOINTS.some((exempt) => baseEndpoint.startsWith(exempt))) {
      return
    }

    // Check if organization is set
    try {
      const organizationStore = useOrganizationStore()
      if (!organizationStore.currentOrganizationId) {
        console.warn(`[useApi] Organization context required for endpoint: ${baseEndpoint}`)
        throw new OrganizationRequiredError(`Organization context is required for ${baseEndpoint}`)
      }
    } catch (error) {
      if (error instanceof OrganizationRequiredError) {
        throw error
      }
      // Store might not be initialized yet - skip check in this case
    }
  }

  /**
   * Fetch with single refresh-and-retry on 401. The buildInit callback is
   * called again on retry so the Authorization header picks up the new token.
   * The current in-flight abort signal is attached to both attempts so an
   * org switch can cancel mid-flight requests, alongside the signal of the caller, if any.
   */
  const fetchWithAuthRetry = async (
    apiUrl: string,
    buildInit: () => RequestInit,
    requestSignal?: AbortSignal
  ): Promise<Response> => {
    const signal = combineSignals(inFlightAbortController.signal, requestSignal)
    try {
      const response = await fetch(apiUrl, { ...buildInit(), signal })
      if (response.status !== 401) return response

      const refreshed = await refreshToken().catch(() => false)
      if (!refreshed) return response

      return await fetch(apiUrl, { ...buildInit(), signal })
    } catch (error) {
      // fetch only rejects on a network/CORS failure (or an abort). Report
      // genuine network failures; aborts are expected (org switch) and skipped.
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        reportApiNetworkError(apiUrl, error)
      }
      throw error
    }
  }

  const setErrorHandler = (handler: cbsetErrorHandler) => {
    errorHandler = handler
  }

  const rejectWithMethod = (response: Response, method: string) => {
    const taggedResponse: ResponseWithMethod = response
    taggedResponse.method = method
    reportApiResponseError(taggedResponse)
    return Promise.reject(taggedResponse)
  }

  const setApiBaseUrl = (url: string) => {
    apiBaseUrl = url
  }

  const endPointUrl = (path?: string, basePart?: string): URL | null => {
    let newUrl: string[] = []

    if (apiBaseUrl) newUrl.push(apiBaseUrl)
    if (baseEndpoint) newUrl.push(baseEndpoint)
    if (basePart) newUrl.push(basePart)
    if (path) newUrl.push(path)

    // Make sure all the parts have stripped away the slashes
    if (joinSlash) {
      newUrl = newUrl.map((it) => {
        it = it.endsWith('/') ? it.slice(0, -1) : it
        it = it.startsWith('/') ? it.slice(1) : it
        return it
      })
    }

    if (newUrl.length === 0) return null

    // Build the URL string, possibly by adding slashes
    const joined = newUrl.join(joinSlash ? '/' : '')
    try {
      return new URL(joined)
    } catch {
      // URL is relative (e.g. when using API proxy) - use current origin as base
      return new URL(`/${joined}`, window.location.origin)
    }
  }

  const buildUrl = (url: string | URL | null): string => {
    if (typeof url === 'string') {
      return (endPointUrl(url) || '').toString()
    } else if (url instanceof URL) {
      return url.toString()
    }
    return ''
  }

  const getHeaders = (additionalHeaders: ApiHeaders | null = null): ApiHeaders => {
    const token = getToken()

    const defaultHeaders: ApiHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }

    // Add Authorization header if token is available
    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`
    }

    // Add X-Organization-Id header if organization is selected
    try {
      const organizationStore = useOrganizationStore()
      if (organizationStore.currentOrganizationId) {
        defaultHeaders['X-Organization-Id'] = organizationStore.currentOrganizationId
      }
    } catch {
      // Store might not be initialized yet during early app startup
      // This is expected - organization header will be added once store is available
    }

    // Merge with additional headers if provided
    return additionalHeaders ? { ...defaultHeaders, ...additionalHeaders } : defaultHeaders
  }

  const apiGet = async (url: string | URL | null, requestOptions?: ApiRequestOptions) => {
    requireOrganizationContext()

    const apiUrl = buildUrl(url)
    if (!apiUrl.length && errorHandler) errorHandler('No API URL found')

    const response = await fetchWithAuthRetry(
      apiUrl,
      () => ({
        method: 'GET',
        headers: getHeaders() as HeadersInit
      }),
      requestOptions?.signal
    )

    if (response.ok) {
      // Guard against empty bodies (204s or endpoints that resolve to
      // undefined) — JSON.parse('') throws a SyntaxError.
      const test = await response.text()
      return test ? JSON.parse(test) : null
    } else {
      return rejectWithMethod(response, 'GET')
    }
  }

  const apiPost = async (url: string | URL | null, properties: unknown) => {
    requireOrganizationContext()

    const apiUrl = buildUrl(url)
    if (!apiUrl.length && errorHandler) errorHandler('No API URL found')

    const response = await fetchWithAuthRetry(apiUrl, () => ({
      method: 'POST',
      headers: getHeaders() as HeadersInit,
      body: JSON.stringify(properties)
    }))

    if (response.ok) {
      return await response.json()
    } else {
      return rejectWithMethod(response, 'POST')
    }
  }

  const apiDelete = async (url: string | URL | null) => {
    requireOrganizationContext()

    const apiUrl = buildUrl(url)
    if (!apiUrl.length && errorHandler) errorHandler('No API URL found')

    const response = await fetchWithAuthRetry(apiUrl, () => ({
      method: 'DELETE',
      headers: getHeaders() as HeadersInit
    }))

    if (response.ok) {
      return await response.json()
    } else {
      return rejectWithMethod(response, 'DELETE')
    }
  }

  const apiPatch = async (url: string | URL | null, properties: unknown) => {
    requireOrganizationContext()

    const apiUrl = buildUrl(url)
    if (!apiUrl.length && errorHandler) errorHandler('No API URL found')

    const response = await fetchWithAuthRetry(apiUrl, () => ({
      method: 'PATCH',
      headers: getHeaders() as HeadersInit,
      body: JSON.stringify(properties)
    }))

    if (response.ok) {
      return await response.json()
    } else {
      return rejectWithMethod(response, 'PATCH')
    }
  }

  const apiPut = async (url: string | URL | null, properties: unknown) => {
    requireOrganizationContext()

    const apiUrl = buildUrl(url)
    if (!apiUrl.length && errorHandler) errorHandler('No API URL found')

    const response = await fetchWithAuthRetry(apiUrl, () => ({
      method: 'PUT',
      headers: getHeaders() as HeadersInit,
      body: JSON.stringify(properties)
    }))

    if (response.ok) {
      return await response.json()
    } else {
      return rejectWithMethod(response, 'PUT')
    }
  }

  /**
   * A fetch() for callers that read the Response themselves, e.g. a streamed body: it adds
   * the auth and organization headers to the caller's own and refreshes the token on 401
   */
  const apiFetch: typeof fetch = async (input, init = {}) => {
    requireOrganizationContext()

    const apiUrl = input instanceof Request ? input.url : input.toString()

    return await fetchWithAuthRetry(
      apiUrl,
      () => {
        const headers = new Headers(init.headers)
        Object.entries(getHeaders()).forEach(([name, value]) => headers.set(name, value))
        return { ...init, headers }
      },
      init.signal ?? undefined
    )
  }

  return {
    setApiBaseUrl,
    setErrorHandler,
    endPointUrl,
    apiGet,
    apiPost,
    apiDelete,
    apiPatch,
    apiPut,
    apiFetch
  }
}
